import { useTranslation } from 'react-i18next';

import logo from '@/assets/images/logo.svg';
import signalIcon from '@/assets/icons/footer/hugeicons_signal.svg';
import whatsappIcon from '@/assets/icons/footer/ic_baseline-whatsapp.svg';
import telegramIcon from '@/assets/icons/footer/ri_telegram-2-line.svg';
import viberIcon from '@/assets/icons/footer/basil_viber-outline.svg';
import emailIcon from '@/assets/icons/footer/ic_outline-email.svg';
import linkedinIcon from '@/assets/icons/footer/linked-in.svg';
import calendarIcon from '@/assets/icons/footer/mynaui_calendar.svg';
import arrow from '@/assets/icons/faq/material-symbols_arrow-back-rounded.svg';

import { LINKS } from '@/constants/LINKS';

import { handleAnchorLinkClick } from '@/utils/handleAnchorLinkClick';
import { getHeaderHeight } from '@/utils/getHeaderHeight';
import { useScreenType } from '@/hooks/useScreenType';

import { Container } from '../Container';
import { LanguageSelect } from '../LanguageSelect';
import { Title } from '../Title';
import { ContainedIcon } from '../ContainedIcon';

import styles from './Footer.module.scss';

export function Footer() {
  const { t } = useTranslation('footer');
  const currentYear = new Date().getFullYear();

  const { screenType } = useScreenType();

  const handleMenuItemClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    handleAnchorLinkClick(e, targetId, getHeaderHeight(screenType));
  };

  return (
    <footer className={styles.footer} id="footer">
      <Container className={styles.footer__container}>
        <div className={styles.footer__content}>
          <div className={styles.footer__column}>
            <a href="/" className={styles.footer__logo}>
              <img src={logo} alt="Logo" />
            </a>
            <p className={styles.footer__text}>{t('logoText')}</p>
            <LanguageSelect isForFooter className={styles.footer__languageSelect} />
          </div>
          <div className={styles.footer__column}>
            <Title className={styles.footer__title}>{t('information.title')}</Title>
            {screenType === 'mobile' && (
              <div className={styles.footer__arrow}>
                <img src={arrow} alt="Arrow" />
              </div>
            )}
            <nav className={styles.footer__menu}>
              <a
                href="#about-us"
                className={styles.menu__item}
                onClick={(e) => handleMenuItemClick(e, 'about-us')}
              >
                {t('information.links.aboutUs')}
              </a>
              <a
                href="#faq"
                className={styles.menu__item}
                onClick={(e) => handleMenuItemClick(e, 'faq')}
              >
                {t('information.links.faq')}
              </a>
              <a
                href="#reviews"
                className={styles.menu__item}
                onClick={(e) => handleMenuItemClick(e, 'reviews')}
              >
                {t('information.links.reviews')}
              </a>
            </nav>
          </div>
          <div className={styles.footer__column}>
            <Title className={styles.footer__title}>{t('services.title')}</Title>
            {screenType === 'mobile' && (
              <div className={styles.footer__arrow}>
                <img src={arrow} alt="Arrow" />
              </div>
            )}
            <nav className={styles.footer__menu}>
              <a
                href="#car-park"
                className={styles.menu__item}
                onClick={(e) => handleMenuItemClick(e, 'car-park')}
              >
                {t('services.links.carPark')}
              </a>
            </nav>
          </div>
          <div className={styles.footer__contacts}>
            <div className={styles.footer__contactsIcons}>
              <a href={LINKS.signal} target="_blank" rel="noreferrer">
                <ContainedIcon
                  className={styles.footer__contactsIcon}
                  src={signalIcon}
                  alt="Signal"
                />
              </a>
              <a href={LINKS.whatsapp} target="_blank" rel="noreferrer">
                <ContainedIcon
                  className={styles.footer__contactsIcon}
                  src={whatsappIcon}
                  alt="WhatsApp"
                />
              </a>
              <a href={LINKS.telegram} target="_blank" rel="noreferrer">
                <ContainedIcon
                  className={styles.footer__contactsIcon}
                  src={telegramIcon}
                  alt="Telegram"
                />
              </a>
              <a href={LINKS.viber} target="_blank" rel="noreferrer">
                <ContainedIcon
                  className={styles.footer__contactsIcon}
                  src={viberIcon}
                  alt="Viber"
                />
              </a>
              <a href={LINKS.linkedin} target="_blank" rel="noreferrer">
                <ContainedIcon
                  className={styles.footer__contactsIcon}
                  src={linkedinIcon}
                  alt="Linked In"
                />
              </a>
            </div>
            <a href={LINKS.email} target="_blank" rel="noreferrer" className={styles.contact}>
              <div className={styles.contact__icons}>
                <ContainedIcon className={styles.contact__icon} src={emailIcon} alt="Email" />
              </div>
              <div className={styles.contact__text}>annatransfer2016@gmail.com</div>
            </a>
            <div className={styles.contact}>
              <div className={styles.contact__icons}>
                <ContainedIcon className={styles.contact__icon} src={calendarIcon} alt="Calendar" />
              </div>
              <div className={styles.contact__text}>{t('workingHours')}</div>
            </div>
          </div>
        </div>
        <div className={styles.footer__footer}>
          © {currentYear} - AnnaTransfer. {t('copyright')}
        </div>
      </Container>
    </footer>
  );
}
